import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

export interface BadgeAward {
  event: NostrEvent;
  /** The "30009:<pubkey>:<d>" reference */
  badgeRef: string;
  /** Awarded pubkeys */
  awardees: string[];
}

function parseBadgeAward(event: NostrEvent): BadgeAward | null {
  const badgeRef = event.tags.find(([t]) => t === 'a')?.[1];
  if (!badgeRef) return null;

  const awardees = event.tags
    .filter(([t]) => t === 'p')
    .map(([, pubkey]) => pubkey)
    .filter(Boolean);

  if (awardees.length === 0) return null;

  return { event, badgeRef, awardees };
}

/** Fetch all badge awards issued by a specific pubkey */
export function useIssuedBadgeAwards(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<BadgeAward[]>({
    queryKey: ['nostr', 'badge-awards-issued', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [8], authors: [pubkey], limit: 200 }],
        { signal: c.signal },
      );

      return events
        .map(parseBadgeAward)
        .filter((b): b is BadgeAward => b !== null);
    },
    enabled: !!pubkey,
    staleTime: 60_000,
  });
}

/** Fetch all badge awards received by a specific pubkey */
export function useReceivedBadgeAwards(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<BadgeAward[]>({
    queryKey: ['nostr', 'badge-awards-received', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [8], '#p': [pubkey], limit: 200 }],
        { signal: c.signal },
      );

      return events
        .map(parseBadgeAward)
        .filter((b): b is BadgeAward => b !== null);
    },
    enabled: !!pubkey,
    staleTime: 60_000,
  });
}
